import React, { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { useData } from '../../contexts';
import { createTheme, Grid, ThemeProvider } from '@mui/material';
import { ArrowNavigation } from '../ArrowNavigation/ArrowNavigation';
import { Situering } from '../Pages/Situering/Situering';
import { MdOutlineMenu } from 'react-icons/md';
import { InsightsEvolutie } from '../Pages/InsightsEvolutie/InsightsEvolutie';
import { InsightsBenchmark } from '../Pages/InsightsBenchmark/InsightsBenchmark';
import { InsightsBenchmarkEvolutie } from '../Pages/InsightsBenchmarkEvolutie/InsightsBenchmarkEvolutie';
import { InsightsCluster } from '../Pages/InsightsCluster/InsightsCluster';
import { SitueringMobile } from '../Pages/SitueringMobile/SitueringMobile';
import { InsightsEvolutieMobile } from '../Pages/InsightsEvolutieMobile/InsightsEvolutieMobile';
import { InsightsBenchmarkMobile } from '../Pages/InsightsBenchmarkMobile/InsightsBenchmarkMobile';
import { InsightsBenchmarkEvolutieMobile } from '../Pages/InsightsBenchmarkEvolutieMobile/InsightsBenchmarkEvolutieMobile';
import { InsightsClusterMobile } from '../Pages/InsightsClusterMobile/InsightsClusterMobile';

interface DataContext {
  additionalData: {
    pageSettings: Queries.config_pages__page_settings;
    allIndicators: Queries.node__indicator[];
  };
}

export default function InsightsSynthesis({
  additionalData,
}: DataContext): React.JSX.Element {
  const {
    selectedGemeente,
    selectedBenchmarks,
    setSelectedBenchmarks,
    setSelectedGemeente,
    colors,
    selectedTopic,
    setSelectedTopic,
    searchParams,
    setSearchParams,
    blobUrl,
    blobUrlRankings,
    blobUrlDashboard,
    blobUrlSamenvattingen,
    blobUrlClusters,
    isDesktop,
    isMobile,
    isTablet,
    interfaceColor,
    selectedIndicatorsInzichten,
    setSelectedIndicatorsInzichten,
  } = useData();

  const defaultIndicators = [
    'AR_03',
    'AR_11',
    'DE_13',
    'KL_06',
    'LO_15',
    'RU_01',
    'SA_01',
    'WE_01',
    'WO_07',
    'WO_09',
    'LO_S_04',
    'LO_S_05',
    'LO_S_11',
    'LO_S_13',
    'SA_S_28',
    'SA_S_32',
    'WO_S_14',
    'WO_S_15',
    'WO_S_33',
    'ZO_S_04',
  ];

  const pathInteractieveTool =
    additionalData?.pageSettings?.relationships?.field_dashboard?.path?.alias;

  const [rankData, setRankData] = useState([]);
  const [hasRunOnce, setHasRunOnce] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [themas, setThemas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [gemeenteLijst, setGemeenteLijst] = useState([]);
  const [benchmarkLijst, setBenchmarkLijst] = useState([]);
  const [indicatorThema, setIndicatorThema] = useState([]);
  const [highlightedRow, setHighlightedRow] = useState(null);
  const [tableHighlightedRow, setTableHighlightedRow] = useState(null);

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);

  const fetcher = (url: any) => fetch(url).then((res) => res.json());

  const swrOptions = {
    // revalidateOnFocus: false,
    // revalidateOnMount: false,
    // revalidateOnReconnect: false,
    // refreshWhenOffline: false,
    // refreshWhenHidden: false,
    // refreshInterval: 0,
  };

  const { data: d_indicator } = useSWR(
    `${blobUrl}d_indicator.json`,
    fetcher,
    swrOptions,
  );
  const { data: d_thema } = useSWR(
    `${blobUrl}d_thema.json`,
    fetcher,
    swrOptions,
  );
  const { data: d_indicator_detail } = useSWR(
    `${blobUrl}d_indicator_detail.json`,
    fetcher,
    swrOptions,
  );
  const { data: d_ruimte } = useSWR(
    `${blobUrl}d_ruimte.json`,
    fetcher,
    swrOptions,
  );
  const { data: d_benchmark } = useSWR(
    `${blobUrl}d_benchmark.json`,
    fetcher,
    swrOptions,
  );
  const { data: f_indicator_thema } = useSWR(
    `${blobUrl}f_indicator_thema.json`,
    fetcher,
    swrOptions,
  );
  const gemeenteRankingFile = `f_ruimte_ranking_${selectedGemeente.id}`;
  const { data: f_ruimte_rankings } = useSWR(
    `${blobUrlRankings}${gemeenteRankingFile}.json`,
    fetcher,
    swrOptions,
  );

  const benchRankingFile = `f_ruimte_ranking_${selectedBenchmarks[0]?.id}`;
  const { data: f_ruimte_rankings_bench } = useSWR(
    `${blobUrlRankings}${benchRankingFile}.json`,
    fetcher,
    swrOptions,
  );
  const { data: f_ranking_summary } = useSWR(
    `${blobUrlRankings}f_ranking_summary.json`,
    fetcher,
    swrOptions,
  );
  const { data: d_cluster } = useSWR(
    `${blobUrlClusters}d_cluster.json`,
    fetcher,
    swrOptions,
  );
  const { data: f_clusters } = useSWR(
    `${blobUrlClusters}f_clusters.json`,
    fetcher,
    swrOptions,
  );
  const theme = createTheme({
    typography: {
      allVariants: {
        fontFamily: 'Flanders Art Sans',
        fontSize: '1.4rem',
      },
    },
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: '14px',
          },
        },
      },
    },
  });

  const situeringSection = useRef(null);
  const insightsEvolutieSection = useRef(null);
  const insightsBenchmarkSection = useRef(null);
  const insightsBenchmarkEvolutieSection = useRef(null);
  const insightsClusterSection = useRef(null);

  function useUrlUpdate(url: string) {
    if (typeof window !== `undefined` && typeof url !== `undefined`) {
      window.history.pushState({}, '', url);
    }
    return;
  }

  const getAndSetRuimteByLabel = (label: any) => {
    if (label !== null) {
      let records = d_ruimte.filter((d: any) => {
        return (
          d.ruimte === label &&
          d.typeRuimte === 'Gemeente' &&
          parseInt(d.isFusie) === 0
        );
      });

      if (records !== undefined && records.length > 0) {
      } else {
        records = d_ruimte.filter((d: any) => {
          return d.id === 41002;
        });
      }

      if (records !== undefined && records.length > 0) {
        let ruimte = structuredClone(records[0]);
        ruimte.id = parseInt(ruimte.id);
        ruimte.label = ruimte.ruimte;

        setSelectedGemeente(ruimte);

        const trimmedUrl = window.location.href.split('/').shift();
        const path =
          additionalData?.pageSettings?.relationships?.field_insights_synthesis
            ?.path?.alias;

        const url = `${trimmedUrl}${path}?gemeente=${ruimte.ruimte}`;
        const param = searchParams.get('indicator');
        if (param) {
          searchParams.delete('indicator');
        }

        setSearchParams((params: any) => {
          params.set('gemeente', ruimte.ruimte);
          return params;
        });

        useUrlUpdate(url);
      }

      if (selectedBenchmarks.id === 2000) {
        let defaultBench = d_ruimte.find(
          (d: any) => parseInt(d.isVlaamsGewest) === 1,
        );

        defaultBench.label = defaultBench.ruimte;
        setSelectedBenchmarks([defaultBench]);
      }
    }
  };

  const prepData = () => {
    const data = f_ruimte_rankings.map((d: any) => {
      d.topLevel = 0;
      d.idIndicatorTop = d.idIndicator;
      if (d.idIndicatorP2 !== null) {
        d.topLevel = 2;
        d.idIndicatorTop = d.idIndicatorP2;
      } else if (d.idIndicatorP1 !== null) {
        d.topLevel = 1;
        d.idIndicatorTop = d.idIndicatorP1;
      }

      const benchRanking = (f_ruimte_rankings_bench as any[]).find(
        (item) => d.joinKey === item.joinKey,
      );

      const indicatorDetail = (d_indicator_detail as any[]).find(
        (item) => parseInt(d.idIndicatorDetail) === parseInt(item.id),
      );

      const indicator = (d_indicator as any[]).find(
        (item) => parseInt(d.idIndicatorTop) === parseInt(item.id),
      );

      const indicatorPart0 = (d_indicator as any[]).find(
        (item) => parseInt(d.idIndicator) === parseInt(item.id),
      );

      const indicatorPart1 = (d_indicator as any[]).find(
        (item) => parseInt(d.idIndicatorP1) === parseInt(item.id),
      );

      const indicatorPart2 = (d_indicator as any[]).find(
        (item) => parseInt(d.idIndicatorP2) === parseInt(item.id),
      );

      const rankingSummary = (f_ranking_summary as any[]).find(
        (item) => d.joinKey === item.joinkey && d.jaar === item.jaar,
      );

      if (d.topLevel === 0) {
        d.label = indicatorPart0?.indicator;
      } else if (d.topLevel === 1) {
        d.label = indicatorPart1?.indicator + ' - ' + indicatorPart0?.indicator;
      } else if (d.topLevel === 2) {
        d.label =
          indicatorPart2?.indicator +
          ' - ' +
          indicatorPart1?.indicator +
          ' - ' +
          indicatorPart0?.indicator;
      }

      d.indicator = indicator.indicator;
      d.idThema = indicator.idThema;

      d.rankNormalised = Math.ceil(
        (d.rankingVlaanderen / rankingSummary?.aantal) * 100,
      );
      d.rankNormalisedBench = benchRanking
        ? Math.ceil(
            (benchRanking.rankingVlaanderen / rankingSummary?.aantal) * 100,
          )
        : null;

      const d3format =
        indicatorDetail && indicatorDetail.numberFormat
          ? indicatorDetail.ratio === '%'
            ? `,.${parseInt(indicatorDetail.decimalen)}%`
            : `,.${parseInt(indicatorDetail.decimalen)}f`
          : ',.0%';

      const row = {
        ido: indicator.code,
        joinKey: d.joinKey,
        idIndicator: d.idIndicator,
        idIndicatorP1: d.idIndicatorP1,
        idIndicatorP2: d.idIndicatorP2,
        idIndicatorTop: d.idIndicatorTop,
        idIndicatorDetail: d.idIndicatorDetail,
        idThema: indicator.idThema,
        indicator: d.label,
        jaar: d.jaar,
        rankTotaal: rankingSummary?.aantal,
        rank: d.rankingVlaanderen,
        rankNormalised: d.rankNormalised,
        value: d.value,
        valueMin3: d.valueMin3,
        valueMin6: d.valueMin6,
        deltaMin3: d.deltaMin3,
        deltaMin6: d.deltaMin6,

        rankBench: benchRanking?.rankingVlaanderen,
        rankNormalisedBench: d.rankNormalisedBench,
        valueBench: benchRanking?.value,
        valueMin3Bench: benchRanking?.valueMin3,
        valueMin6Bench: benchRanking?.valueMin6,
        deltaMin3Bench: benchRanking?.deltaMin3,
        deltaMin6Bench: benchRanking?.deltaMin6,

        // deltaGemeenteBench: benchRanking?.value
        //   ? (d.value - benchRanking.value) /
        //     ((d.value + benchRanking.value) / 2)
        //   : null,

        deltaGemeenteBench: benchRanking?.value
          ? (d.value - benchRanking.value) / benchRanking.value
          : null,

        valueMin: rankingSummary?.minimum,
        valueMax: rankingSummary?.maximum,

        [selectedGemeente.ruimte]: d.value,
        [selectedBenchmarks[0].ruimte]: benchRanking?.value,

        evoGemeenteMin3:
          isNaN((d?.value - d?.valueMin3) / d?.value) ||
          Math.abs((d?.value - d?.valueMin3) / d?.value) === Infinity
            ? 0
            : (d?.value - d?.valueMin3) / d?.value,

        evoBenchMin3:
          isNaN(
            (benchRanking?.value - benchRanking?.valueMin3) /
              benchRanking?.value,
          ) ||
          Math.abs(
            (benchRanking?.value - benchRanking?.valueMin3) /
              benchRanking?.value,
          ) === Infinity
            ? 0
            : (benchRanking?.value - benchRanking?.valueMin3) /
              benchRanking?.value,

        evoGemeenteMin6:
          isNaN((d?.value - d?.valueMin6) / d?.value) ||
          Math.abs((d?.value - d?.valueMin6) / d?.value) === Infinity
            ? 0
            : (d?.value - d?.valueMin6) / d?.value,

        evoBenchMin6:
          isNaN(
            (benchRanking?.value - benchRanking?.valueMin6) /
              benchRanking?.value,
          ) ||
          Math.abs(
            (benchRanking?.value - benchRanking?.valueMin6) /
              benchRanking?.value,
          ) === Infinity
            ? 0
            : (benchRanking?.value - benchRanking?.valueMin6) /
              benchRanking?.value,

        d3format: d3format,
        eenheid: indicatorDetail.eenheid,
        ratio: indicatorDetail.ratio,
        survey: indicatorDetail.survey,

        y: 1,
        z: 100,
      };

      return row;
    });

    // TODO: filter on selection of selectionpanel
    let filteredData = [];

    if (selectedIndicatorsInzichten.length > 0) {
      filteredData = data.filter((d: any) => {
        return selectedIndicatorsInzichten.includes(d.idIndicatorTop);
      });
    }

    filteredData.sort(function (a: any, b: any) {
      return a.indicator.localeCompare(b.indicator);
    });

    let i = 1;
    filteredData.map((d: any) => {
      d.id = i;
      i += 1;
      return d;
    });

    setRankData(filteredData);
  };

  useEffect(() => {
    if (
      hasRunOnce &&
      d_indicator &&
      d_indicator_detail &&
      d_thema &&
      f_ruimte_rankings &&
      f_ruimte_rankings_bench &&
      f_ranking_summary &&
      f_indicator_thema
    ) {
      setThemas(
        d_thema.filter((d: any) => {
          return d.id !== 0;
        }),
      );

      prepData();
    }
  }, [
    hasRunOnce,
    d_indicator,
    d_thema,
    d_indicator_detail,
    f_ruimte_rankings,
    f_ruimte_rankings_bench,
    f_ranking_summary,
    f_indicator_thema,
    selectedIndicatorsInzichten,
  ]);

  useEffect(() => {
    if (defaultIndicators && d_ruimte && d_indicator && !hasRunOnce) {
      if (selectedBenchmarks.length > 0) {
        setSelectedBenchmarks((prev) => {
          return prev.filter((d: any) => {
            return d.isFusie === 0;
          });
        });
      }

      const filteredIds = d_indicator
        .filter((d: any) => {
          return defaultIndicators.includes(d.code);
        })
        .map((item: any) => item.id);

      setSelectedIndicatorsInzichten(filteredIds);

      const searchKeywordCity = searchParams.get('gemeente');
      const searchKeywordIndicator = searchParams.get('indicator');

      if (searchKeywordCity === null && selectedGemeente.id === -1) {
        const defaultGemeente = d_ruimte.find((d: any) => d.ruimte === 'Aalst');
        defaultGemeente.label = defaultGemeente.ruimte;

        if (selectedBenchmarks.id === 2000) {
          let defaultBench = d_ruimte.find(
            (d: any) => parseInt(d.isVlaamsGewest) === 1,
          );
          defaultBench.label = defaultBench.ruimte;
          setSelectedBenchmarks([defaultBench]);
        }

        setSelectedGemeente(defaultGemeente);
        setToggled(true);

        const param = searchParams.get('indicator');
        if (param) {
          searchParams.delete('indicator');
        }

        setSearchParams((params: any) => {
          params.set('gemeente', defaultGemeente.ruimte);
          return params;
        });

        const trimmedUrl = window.location.href.split('/').shift();
        const path =
          additionalData?.pageSettings?.relationships?.field_insights_synthesis
            ?.path?.alias;

        const url = `${trimmedUrl}${path}?gemeente=${defaultGemeente.ruimte}`;
        useUrlUpdate(url);
      } else {
        getAndSetRuimteByLabel(searchKeywordCity);
      }

      setHasRunOnce(true);
    }
  }, [hasRunOnce, d_ruimte, d_indicator, selectedGemeente, defaultIndicators]);

  useEffect(() => {
    if (d_ruimte && d_benchmark && hasRunOnce) {
      const vlgewest = d_ruimte.filter((d: any) => {
        return parseInt(d?.isVlaamsGewest) === 1;
      });

      const centrumSteden = d_ruimte.filter((d: any) => {
        return parseInt(d?.is13CentrumSteden) === 1;
      });

      const facil = d_ruimte.filter((d: any) => {
        return parseInt(d?.isFaciliteitenGemeente) === 1;
      });

      const nietFacil = d_ruimte.filter((d: any) => {
        return parseInt(d?.isNietFaciliteitenGemeente) === 1;
      });

      const vlRand = d_ruimte.filter((d: any) => {
        return parseInt(d?.isVlaamseRand) === 1;
      });

      const belfius = d_ruimte.filter((d: any) => {
        return (
          parseInt(d.id) === parseInt(selectedGemeente?.idRuimteBelfiusCluster)
        );
      });

      const buurGem = d_ruimte.filter((d: any) => {
        return (
          parseInt(d.id) === parseInt(selectedGemeente?.idRuimteBuurgemeenten)
        );
      });

      const gemGrootte = d_ruimte.filter((d: any) => {
        return (
          parseInt(d.id) === parseInt(selectedGemeente?.idRuimteGemeenteGrootte)
        );
      });

      const prov = d_ruimte.filter((d: any) => {
        return parseInt(d.id) === parseInt(selectedGemeente?.idRuimteProvincie);
      });

      const regio = d_ruimte.filter((d: any) => {
        return parseInt(d.id) === parseInt(selectedGemeente?.idRuimteRegio);
      });

      const allBench = vlgewest
        .concat(
          prov,
          regio,
          belfius,
          gemGrootte,
          buurGem,
          centrumSteden,
          vlRand,
          facil,
          nietFacil,
        )
        .map((d: any) => {
          d.bench = 1;
          return d;
        });

      const sortedCities = d_ruimte
        .filter((d: any) => {
          return (
            parseInt(d.isVlaamseGemeente) === 1 && parseInt(d.isFusie) === 0
          );
        })

        .sort((a: any, b: any) => {
          let fa = a.ruimte.toLowerCase(),
            fb = b.ruimte.toLowerCase();

          if (fa < fb) {
            return -1;
          }
          if (fa > fb) {
            return 1;
          }
          return 0;
        })
        .map((d: any) => {
          d.bench = 0;
          return d;
        });

      // const citiesBench = vlgewest.concat(sortedCities);
      const citiesBench = allBench.concat(sortedCities).filter((d: any) => {
        return parseInt(d.id) !== parseInt(selectedGemeente?.id);
      });

      // compare currently selected benchmarks with benchmarks in list to see if there are no illegal selections
      const uniqueBenchIDs = [...new Set(allBench.map((d: any) => d.id))];

      let selBench = selectedBenchmarks.filter((d: any) => {
        return parseInt(d.isGemeente) || uniqueBenchIDs.includes(d.id);
      });

      if (selBench.length === 0) {
        selBench = d_ruimte.find((d: any) => parseInt(d.isVlaamsGewest) === 1);
        selBench.label = selBench.ruimte;
        selBench = [selBench];
      }

      setGemeenteLijst(sortedCities);
      setBenchmarkLijst(citiesBench);
      setSelectedBenchmarks(selBench);

      const trimmedUrl = window.location.href.split('/').shift();
      const path =
        additionalData?.pageSettings?.relationships?.field_insights_synthesis
          ?.path?.alias;

      const url = `${trimmedUrl}${path}?gemeente=${selectedGemeente.ruimte}`;
      const param = searchParams.get('indicator');
      if (param) {
        searchParams.delete('indicator');
      }

      setSearchParams((params) => {
        params.set('gemeente', selectedGemeente.ruimte);
        return params;
      });
      useUrlUpdate(url);
    }
  }, [d_ruimte, d_benchmark, hasRunOnce, selectedGemeente]);

  useEffect(() => {
    const indThema: any = f_indicator_thema?.map((d: any) => {
      const indicator = d_indicator?.find((a: any) => {
        return a.id === d.idIndicator;
      });
      d.indicator = indicator?.indicator;
      return d;
    });

    indThema?.sort((a: any, b: any) => {
      if (!(a && a.indicator && b && b.indicator)) return;

      const stringA = a.indicator.toUpperCase(); // ignore upper and lowercase
      const stringB = b.indicator.toUpperCase(); // ignore upper and lowercase
      if (stringA > stringB) return 1;
      if (stringA < stringB) return -1;
      return 0;
    });

    setIndicatorThema(indThema);
  }, [d_indicator, f_indicator_thema]);

  const contentJSX = (
    <>
      <div style={{ width: '100%' }}>
        <Grid
          item
          xs={12}
          p={2}
          pt={2}
          pl={14}
          sx={{ position: 'relative', width: '50%' }}
        >
          <p style={{ fontSize: '0.8em' }}>
            <u>Opgelet</u>: Voor de nieuwe fusiegemeenten zijn nog niet alle
            registerindicatoren beschikbaar, als gevolg van nieuwe berekeningen
            bij de dataproducent. Je kan nagaan voor welke registerindicatoren
            momenteel al data beschikbaar zijn{' '}
            <a
              href={
                process.env.BLOB_STORAGE_BASE_URL +
                'documentatie/fusies/gemeentestadsmonitor_data_fusiegemeenten.xlsx'
              }
              target="_blank"
            >
              in dit overzicht
            </a>
            . De registerdata van de vroegere gemeenten kan je downloaden{' '}
            <a href={'download-alle-cijfers'} target="_blank">
              op deze pagina
            </a>
            . Voor de survey-indicatoren kan je de gegevens van de vroegere
            gemeenten terugvinden{' '}
            <a
              href={
                additionalData?.pageSettings?.relationships?.field_fusion_data
                  ?.path?.alias
              }
              target="_blank"
            >
              op deze pagina
            </a>
            .
          </p>
        </Grid>
        <Grid
          item
          xs={12}
          p={2}
          pt={0}
          height={'100vh'}
          minHeight={'700px'}
          sx={{ position: 'relative', width: '100%' }}
          ref={situeringSection}
        >
          <ArrowNavigation
            up={null}
            down={insightsBenchmarkSection}
            current={situeringSection}
          />

          <Situering
            data={rankData}
            pathInteractieveTool={pathInteractieveTool}
            toggled={toggled}
            open={open}
            setOpen={setOpen}
            themas={themas}
            indicatorThema={indicatorThema}
            gemeenteLijst={gemeenteLijst}
            benchmarkLijst={benchmarkLijst}
            setBenchmarkLijst={setBenchmarkLijst}
            d_ruimte={d_ruimte}
            highlightedRow={highlightedRow}
            setHighlightedRow={setHighlightedRow}
            tableHighlightedRow={tableHighlightedRow}
            setTableHighlightedRow={setTableHighlightedRow}
          ></Situering>
        </Grid>
      </div>

      <div style={{ width: '100%' }}>
        <Grid
          item
          xs={12}
          p={2}
          pt={0}
          height={'100vh'}
          minHeight={'700px'}
          sx={{ position: 'relative', width: '100%' }}
          ref={insightsBenchmarkSection}
        >
          <ArrowNavigation
            up={situeringSection}
            down={insightsEvolutieSection}
            current={insightsBenchmarkSection}
          />

          <InsightsBenchmark
            data={rankData}
            pathInteractieveTool={pathInteractieveTool}
            toggled={toggled}
            open={open}
            setOpen={setOpen}
            themas={themas}
            indicatorThema={indicatorThema}
            gemeenteLijst={gemeenteLijst}
            benchmarkLijst={benchmarkLijst}
            setBenchmarkLijst={setBenchmarkLijst}
            d_ruimte={d_ruimte}
            highlightedRow={highlightedRow}
            setHighlightedRow={setHighlightedRow}
            tableHighlightedRow={tableHighlightedRow}
            setTableHighlightedRow={setTableHighlightedRow}
          ></InsightsBenchmark>
        </Grid>
      </div>

      <div style={{ width: '100%' }}>
        <Grid
          item
          xs={12}
          p={2}
          pt={0}
          height={'100vh'}
          minHeight={'700px'}
          sx={{ position: 'relative', width: '100%' }}
          ref={insightsEvolutieSection}
        >
          <ArrowNavigation
            up={insightsBenchmarkSection}
            down={insightsBenchmarkEvolutieSection}
            current={insightsEvolutieSection}
          />

          <InsightsEvolutie
            data={rankData}
            pathInteractieveTool={pathInteractieveTool}
            toggled={toggled}
            open={open}
            setOpen={setOpen}
            themas={themas}
            indicatorThema={indicatorThema}
            gemeenteLijst={gemeenteLijst}
            benchmarkLijst={benchmarkLijst}
            setBenchmarkLijst={setBenchmarkLijst}
            d_ruimte={d_ruimte}
            highlightedRow={highlightedRow}
            setHighlightedRow={setHighlightedRow}
            tableHighlightedRow={tableHighlightedRow}
            setTableHighlightedRow={setTableHighlightedRow}
          ></InsightsEvolutie>
        </Grid>
      </div>

      <div style={{ width: '100%' }}>
        <Grid
          item
          xs={12}
          p={2}
          pt={0}
          height={'100vh'}
          minHeight={'700px'}
          sx={{ position: 'relative', width: '100%' }}
          ref={insightsBenchmarkEvolutieSection}
        >
          <ArrowNavigation
            up={insightsEvolutieSection}
            down={insightsClusterSection}
            current={insightsBenchmarkEvolutieSection}
          />

          <InsightsBenchmarkEvolutie
            data={rankData}
            pathInteractieveTool={pathInteractieveTool}
            toggled={toggled}
            open={open}
            setOpen={setOpen}
            themas={themas}
            indicatorThema={indicatorThema}
            gemeenteLijst={gemeenteLijst}
            benchmarkLijst={benchmarkLijst}
            setBenchmarkLijst={setBenchmarkLijst}
            d_ruimte={d_ruimte}
            highlightedRow={highlightedRow}
            setHighlightedRow={setHighlightedRow}
            tableHighlightedRow={tableHighlightedRow}
            setTableHighlightedRow={setTableHighlightedRow}
          ></InsightsBenchmarkEvolutie>
        </Grid>
      </div>

      <div style={{ width: '100%' }}>
        <Grid
          item
          xs={12}
          p={2}
          pt={0}
          height={'100vh'}
          minHeight={'700px'}
          sx={{ position: 'relative', width: '100%' }}
          ref={insightsClusterSection}
        >
          <ArrowNavigation
            up={insightsBenchmarkEvolutieSection}
            down={null}
            current={insightsClusterSection}
          />

          <InsightsCluster
            d_cluster={d_cluster}
            f_clusters={f_clusters}
            pathInteractieveTool={pathInteractieveTool}
            toggled={toggled}
            open={open}
            setOpen={setOpen}
            themas={themas}
            indicatorThema={indicatorThema}
            gemeenteLijst={gemeenteLijst}
            benchmarkLijst={benchmarkLijst}
            setBenchmarkLijst={setBenchmarkLijst}
            d_ruimte={d_ruimte}
            d_thema={d_thema}
            clusterAnalysePath={
              additionalData?.pageSettings?.relationships
                ?.field_insights_synthesis?.path?.alias
            }
          ></InsightsCluster>
        </Grid>
      </div>
    </>
  );

  const contentJSXMobile = (
    <div style={{ width: '100%' }}>
      <Grid container style={{ width: '100%' }}>
        <Grid
          item
          xs={12}
          p={2}
          pt={2}
          pl={5}
          pr={4}
          sx={{ position: 'relative' }}
        >
          <p style={{ fontSize: '0.8em' }}>
            <u>Opgelet</u>: Voor de nieuwe fusiegemeenten zijn nog niet alle
            registerindicatoren beschikbaar, als gevolg van nieuwe berekeningen
            bij de dataproducent. Je kan nagaan voor welke registerindicatoren
            momenteel al data beschikbaar zijn{' '}
            <a
              href={
                process.env.BLOB_STORAGE_BASE_URL +
                'documentatie/fusies/gemeentestadsmonitor_data_fusiegemeenten.xlsx'
              }
              target="_blank"
            >
              in dit overzicht
            </a>
            . De registerdata van de vroegere gemeenten kan je downloaden{' '}
            <a href={'download-alle-cijfers'} target="_blank">
              op deze pagina
            </a>
            . Voor de survey-indicatoren kan je de gegevens van de vroegere
            gemeenten terugvinden{' '}
            <a
              href={
                additionalData?.pageSettings?.relationships?.field_fusion_data
                  ?.path?.alias
              }
              target="_blank"
            >
              op deze pagina
            </a>
            .
          </p>
        </Grid>
        <Grid item xs={12} marginBottom={'50px'}>
          <SitueringMobile
            data={rankData}
            pathInteractieveTool={pathInteractieveTool}
            toggled={toggled}
            open={open}
            setOpen={setOpen}
            themas={themas}
            indicatorThema={indicatorThema}
            gemeenteLijst={gemeenteLijst}
            benchmarkLijst={benchmarkLijst}
            setBenchmarkLijst={setBenchmarkLijst}
            d_ruimte={d_ruimte}
            highlightedRow={highlightedRow}
            setHighlightedRow={setHighlightedRow}
            tableHighlightedRow={tableHighlightedRow}
            setTableHighlightedRow={setTableHighlightedRow}
          ></SitueringMobile>
        </Grid>

        <Grid item xs={12} marginBottom={'50px'}>
          <InsightsBenchmarkMobile
            data={rankData}
            pathInteractieveTool={pathInteractieveTool}
            toggled={toggled}
            open={open}
            setOpen={setOpen}
            themas={themas}
            indicatorThema={indicatorThema}
            gemeenteLijst={gemeenteLijst}
            benchmarkLijst={benchmarkLijst}
            setBenchmarkLijst={setBenchmarkLijst}
            d_ruimte={d_ruimte}
            highlightedRow={highlightedRow}
            setHighlightedRow={setHighlightedRow}
            tableHighlightedRow={tableHighlightedRow}
            setTableHighlightedRow={setTableHighlightedRow}
          ></InsightsBenchmarkMobile>
        </Grid>

        <Grid item xs={12} marginBottom={'50px'}>
          <InsightsEvolutieMobile
            data={rankData}
            pathInteractieveTool={pathInteractieveTool}
            toggled={toggled}
            open={open}
            setOpen={setOpen}
            themas={themas}
            indicatorThema={indicatorThema}
            gemeenteLijst={gemeenteLijst}
            benchmarkLijst={benchmarkLijst}
            setBenchmarkLijst={setBenchmarkLijst}
            d_ruimte={d_ruimte}
            highlightedRow={highlightedRow}
            setHighlightedRow={setHighlightedRow}
            tableHighlightedRow={tableHighlightedRow}
            setTableHighlightedRow={setTableHighlightedRow}
          ></InsightsEvolutieMobile>
        </Grid>

        <Grid item xs={12} marginBottom={'50px'}>
          <InsightsBenchmarkEvolutieMobile
            data={rankData}
            pathInteractieveTool={pathInteractieveTool}
            toggled={toggled}
            open={open}
            setOpen={setOpen}
            themas={themas}
            indicatorThema={indicatorThema}
            gemeenteLijst={gemeenteLijst}
            benchmarkLijst={benchmarkLijst}
            setBenchmarkLijst={setBenchmarkLijst}
            d_ruimte={d_ruimte}
            highlightedRow={highlightedRow}
            setHighlightedRow={setHighlightedRow}
            tableHighlightedRow={tableHighlightedRow}
            setTableHighlightedRow={setTableHighlightedRow}
          ></InsightsBenchmarkEvolutieMobile>
        </Grid>

        <Grid item xs={12} marginBottom={'50px'}>
          <InsightsClusterMobile
            d_cluster={d_cluster}
            f_clusters={f_clusters}
            pathInteractieveTool={pathInteractieveTool}
            toggled={toggled}
            open={open}
            setOpen={setOpen}
            themas={themas}
            indicatorThema={indicatorThema}
            gemeenteLijst={gemeenteLijst}
            benchmarkLijst={benchmarkLijst}
            setBenchmarkLijst={setBenchmarkLijst}
            d_ruimte={d_ruimte}
            d_thema={d_thema}
            clusterAnalysePath={
              additionalData?.pageSettings?.relationships
                ?.field_insights_synthesis?.path?.alias
            }
          ></InsightsClusterMobile>
        </Grid>
      </Grid>
    </div>
  );

  return (
    <section>
      <ThemeProvider theme={theme}>
        <Grid
          container
          className="vl-typography background-0"
          minHeight="500px"
          pt={2}
        >
          {d_indicator &&
          d_thema &&
          d_indicator_detail &&
          d_ruimte &&
          d_benchmark &&
          f_indicator_thema &&
          f_ranking_summary &&
          gemeenteRankingFile &&
          d_cluster &&
          selectedBenchmarks.length > 0 &&
          f_clusters ? (
            isDesktop ? (
              contentJSX
            ) : (
              contentJSXMobile
            )
          ) : (
            <div>{<p></p>}</div>
          )}
        </Grid>
      </ThemeProvider>
    </section>
  );
}
